import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { NavLink as RouterLink, Route, useLocation } from 'react-router-dom';

import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Breadcrumbs, Grid, Typography, useMediaQuery } from '@mui/material';
import { AccountStage } from '@provider-portal/common/services/types/account';
import { $tid } from '@provider-portal/common/utils/testIdConfig';
import { onboardingRoute, routeWidgetEntries } from 'AppRoutes';
import { PromotionsEntry } from 'PromotionsEntry';
import CustomerSupport from 'widgets/CustomerSupport/CustomerSupport';
import { OnboardingProviderSupport } from 'widgets/OnboardingProvidersSupport/OnboardingProviderSupport';

import { selectAccountStage } from 'rdx/account';

import { useBreadcrumbs } from 'root/navigation/useBreadcrumbs';
import { useTitle } from 'root/navigation/useTitle';

export const MainContent: React.FC = ({ children }) => {
  useTitle();
  const intl = useIntl();
  const { breadcrumbsItems } = useBreadcrumbs();
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const accountStage = useSelector(selectAccountStage);

  const setRef = useCallback((ref) => {
    if (ref) {
      setContainerRef(ref);
    }
  }, []);

  useEffect(() => {
    if (containerRef) {
      const scrollContainer = containerRef as HTMLElement;
      scrollContainer.scrollTop = 0;
    }
  }, [containerRef, location.pathname]);

  const isAccountActivated = useMemo(() => accountStage === AccountStage.ACTIVATED, [accountStage]);
  const isOnboardingPage = useMemo(() => {
    return location.pathname.match(onboardingRoute);
  }, [location.pathname]);

  const isExtraSmallScreen = useMediaQuery('(max-width: 380px)');

  return (
    <main data-testid="home-screen" ref={setRef} className="mainContent">
      <Grid
        sx={{
          ml: { xl: '250px' },
          px: { xs: isExtraSmallScreen ? 1 : 3, sm: 3, lg: 6 },
          pt: { xs: 2, lg: 4 },
          pb: { xs: 4, lg: 12 },
        }}
      >
        <Grid item xs={12} className="pageContent">
          <Breadcrumbs
            data-testid={$tid.components.Breadcrumbs}
            aria-label="breadcrumb"
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{ py: 1 }}
          >
            {breadcrumbsItems.map((item, index, arr) => {
              const text = (
                <Typography color={index === arr.length - 1 ? 'textPrimary' : 'textSecondary'}>
                  {intl.formatMessage({ id: item.titleIntlId })}
                </Typography>
              );
              return (
                <div key={item.id}>
                  {item.href ? (
                    <RouterLink color="secondary" to={item.href}>
                      {text}
                    </RouterLink>
                  ) : (
                    text
                  )}
                </div>
              );
            })}
          </Breadcrumbs>
          <Box mt={isOnboardingPage ? 0 : 5}>
            {isAccountActivated && (
              <Box>
                {routeWidgetEntries.map(([key, value]) => {
                  return value ? <Route exact path={key} key={key} component={value} /> : null;
                })}
                <PromotionsEntry />
              </Box>
            )}
            {children}
          </Box>
        </Grid>
      </Grid>
      {isAccountActivated ? <CustomerSupport /> : <OnboardingProviderSupport />}
    </main>
  );
};
